import React from 'react';
import { Badge } from 'src/design/atoms/Badge';
import { Loader } from 'src/design/atoms/Loader';
import { Text } from 'src/design/atoms/Text';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import { Table } from 'src/design/molecules/Table';
import { useWorkflowNotificationHistory } from 'src/api/queries/workflowNotifications/useWorkflowNotificationHistory';
import { useResendWorkflowNotification } from 'src/api/queries/workflowNotifications/useWorkflowNotificationMutations';
import { NotificationHistoryDto } from 'src/api/FiretrolApiClient';
import { formatDate } from 'src/utils';

/** A resend clones the row's recipients, so a row without any (e.g. skipped for no recipients) can only fail. */
const hasRecipients = (row: NotificationHistoryDto): boolean =>
  row.to.length + row.cc.length + row.bcc.length > 0;

export interface INotificationHistoryProps {
  /** Resource type registered on the NotificationsController (e.g. "change", "meeting"). */
  resourceType: string;
  jobId: number;
  entityId: string;
  /** Singular noun for the record used in the empty-state copy (e.g. "change"). */
  entityNoun: string;
}

/** @registry Email notification history table for one record, with per-row Resend and loading/empty states, parameterized by resourceType. */
export const NotificationHistory: React.FC<INotificationHistoryProps> = ({
  resourceType,
  jobId,
  entityId,
  entityNoun,
}) => {
  const { data, isLoading: loading } = useWorkflowNotificationHistory(
    resourceType,
    jobId,
    entityId,
  );
  const history = data ?? [];
  const resendNotification = useResendWorkflowNotification();

  if (loading) return <Loader />;

  if (history.length === 0) {
    return (
      <Text c="dimmed">{`No emails have been sent for this ${entityNoun}.`}</Text>
    );
  }

  return (
    <Table>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Status</Table.Th>
          <Table.Th>Event</Table.Th>
          <Table.Th>Subject</Table.Th>
          <Table.Th>Recipients</Table.Th>
          <Table.Th>Sent</Table.Th>
          <Table.Th />
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {history.map((row) => (
          <Table.Tr key={row.id}>
            <Table.Td>
              <Badge>{row.status}</Badge>
            </Table.Td>
            <Table.Td>{row.eventType}</Table.Td>
            <Table.Td>{row.subject}</Table.Td>
            <Table.Td>{row.to.map((r) => r.address).join(', ')}</Table.Td>
            <Table.Td>{row.sentAt ? formatDate(row.sentAt) : '—'}</Table.Td>
            <Table.Td>
              {hasRecipients(row) && (
                <SecondaryButton
                  onClick={() =>
                    resendNotification.mutate({
                      resourceType,
                      jobId,
                      entityId,
                      logId: row.id,
                    })
                  }
                >
                  Resend
                </SecondaryButton>
              )}
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
};
